import { useIntl } from 'react-intl';

import { CargoCharacteristicDto, getCargoCharacteristicTranslationId } from 'Modules/Cargo';
import { store } from 'Models/Store';
import { Flex } from 'Views/Components/Flex/Flex';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useWizardFetchedDataStore } from '../../BookingWizardState/BookingWizardFetchedData';
import { VehicleTabActionCreators, wizardDataVehicleTabReducer } from './Reducers/vehicleTabReducer';

type VehicleTabAction = ReturnType<typeof VehicleTabActionCreators.changeCharacteristic>;

function VehicleCargoCharacteristicDetails() {
	const intl = useIntl();
	const { wizardData, setWizardData } = useBookingWizardDataStore();
	const vehicleTabData = useWizardFetchedDataStore(x => x.vehicleTabData);

	if (wizardData.innerWizardData.type !== 'vehicle') {
		return <></>;
	}

	const { cargoTypeId } = wizardData.innerWizardData;
	const inheritedIds = store.cargoTypeCharacteristicsMap[cargoTypeId ?? ''] ?? [];

	// Selectable characteristics are always a per-booking choice. When the customer has a cargo profile for
	// this cargo type, the inherited characteristics (and any non-selectable additions / removals) are
	// governed by the profile and applied automatically on the server — so we hide the inherited rows and
	// show only the selectable add-ons to pick. Without a profile, inherited ones are shown checked + locked.
	const profileCargoTypeIds = vehicleTabData?.profileCargoTypeIds ?? [];
	const hasProfile = !!cargoTypeId && profileCargoTypeIds.includes(cargoTypeId);

	const characteristicsToShow: CargoCharacteristicDto[] = hasProfile
		? store.selectableCargoCharacteristics.filter(c => !inheritedIds.includes(c.id))
		: [...store.selectableCargoCharacteristics];
	if (characteristicsToShow.length === 0) {
		return <></>;
	}

	const applyAction = (action: VehicleTabAction) => {
		const newData = wizardDataVehicleTabReducer(wizardData, action);
		if (newData) {
			setWizardData(newData);
		}
	};

	const selectedCharacteristicIds = wizardData.innerWizardData.cargoCharacteristics.map(x => x.characteristicId);

	return (
		<Flex direction="col" flex={1} gap="xs">
			{characteristicsToShow.map(characteristic => {
				const label = intl.formatMessage({
					id: getCargoCharacteristicTranslationId(characteristic),
					defaultMessage: characteristic.name,
				});

				const isInherited = inheritedIds.includes(characteristic.id);

				if (isInherited) {
					// Inherited from the cargo type: always applies, shown checked and locked.
					return (
						<CheckboxInput
							className="vehicle-characteristic-checkbox"
							key={characteristic.id}
							label={label}
							disabled
							checked
							onChange={() => {}}
						/>
					);
				}

				// Selectable add-on characteristic (not inherited): toggleable.
				return (
					<CheckboxInput
						className="vehicle-characteristic-checkbox"
						key={characteristic.id}
						label={label}
						checked={selectedCharacteristicIds.includes(characteristic.id)}
						onChange={() =>
							applyAction(
								VehicleTabActionCreators.changeCharacteristic({
									characteristicId: characteristic.id,
									name: characteristic.name,
								}),
							)
						}
					/>
				);
			})}
		</Flex>
	);
}

export default VehicleCargoCharacteristicDetails;
